import React, { createContext, ReactNode, useEffect, useState } from 'react'

import catchError from 'utils/catchError'

import AccountManagerBar from 'components/core-adoption/builder/AccountManagerBar'
import Navigation from 'components/core-adoption/builder/Navigation'
import HeroSection from 'components/core-adoption/builder/HeroSection'
import LanguageToggle from 'components/core-adoption/builder/LanguageToggle'
import Videos from 'components/core-adoption/builder/Videos'
import ThemeMap from 'components/core-adoption/builder/ThemeMap'
import KnowledgeBuilding from 'components/core-adoption/builder/KnowledgeBuilding'
import Phonics from 'components/core-adoption/builder/Phonics'
import FormativeAssessment from 'components/core-adoption/builder/FormativeAssessment'
import EnglishToolkits from 'components/core-adoption/builder/EnglishToolkits'
import EnglishToolkitsSecondary from 'components/core-adoption/builder/EnglishToolkitsSecondary'
import FormativeAssessmentSpanish from 'components/core-adoption/builder/FormativeAssessmentSpanish'
import SpanishToolkits from 'components/core-adoption/builder/SpanishToolkits'
import DayYear from 'components/core-adoption/builder/DayYear'
import DigitalSolutionsTabs from 'components/core-adoption/builder/DigitalSolutionsTabs'
import DigitalSolutionsAccess from 'components/core-adoption/builder/DigitalSolutionsAccess'
import ProfessionalLearning from 'components/core-adoption/builder/ProfessionalLearning'
import Reviews from 'components/core-adoption/builder/Reviews'
import AdditionalResources from 'components/core-adoption/builder/AdditionalResources'
import { useUser } from '@supabase/auth-helpers-react'
import { supabase } from 'clients/supabase'
import { CoreAdoption, Region, Resource, StandardlessResource, Toolkit, UserProfile, UserRole } from 'common-types'
import stringToLowerCaseDashString from 'utils/stringToLowerCaseDashString'
import formatPhoneNumber from 'utils/formatPhoneNumber'
import toast from 'react-hot-toast'
import { wait } from 'utils/wait'
import { isBrowser } from 'utils/isBrowser'
import axios from 'axios'
import LogRocket from 'logrocket'
import { initLogRocket } from 'components/core-adoption/functions/initLogRocket'
import { Json } from 'database.types'
import * as Sentry from '@sentry/gatsby'
import Accelerator from 'components/core-adoption/builder/Accelerator'
import HeroSectionIntervention from 'components/core-adoption/builder/HeroSectionIntervention'
import VideosIntervention from 'components/core-adoption/builder/VideosIntervention'
import OneHundredBookChallengeEnglish from 'components/core-adoption/builder/HundredBookChallengeEnglish'
import OneHundredBookChallengeSpanish from 'components/core-adoption/builder/HundredBookChallengeSpanish'
import ThemeMapMiddleSchool from 'components/core-adoption/builder/ThemeMapMiddleSchool'
import AcceleratorElementary from 'components/core-adoption/builder/AcceleratorElementary'
import AcceleratorSecondary from 'components/core-adoption/builder/AcceleratorSecondary'
import FoundationalSkillsToolkits from 'components/core-adoption/builder/FoundationalSkillsToolkits'
import ENILToolkits from 'components/core-adoption/builder/ENILToolkits'
import IRLAToolkits from 'components/core-adoption/builder/IRLAToolkits'
import SecondaryToolkits from 'components/core-adoption/builder/SecondaryToolkits'
import Decodables from 'components/core-adoption/builder/Decodables'
import IRLAStarterKit from 'components/core-adoption/builder/IRLAStarterKits'
import ENILStarterKit from 'components/core-adoption/builder/ENILStarterKits'
import ThemeMapFSC from 'components/core-adoption/builder/ThemeMapFSC'

interface ICoreAdoptionContext {
	account: BuilderState | null
	accountManager: Profile | null
	language: 'english' | 'spanish'
	setLanguage: (language: 'english' | 'spanish') => void
	standard: 'ccss' | 'teks'
	visitor: { id: number | null; name: string; email: string; phone: string; title: string }
	handleSetVisitor: (key: keyof ICoreAdoptionContext['visitor'], value: string) => void
	sessionId: number | null
	setSessionId: (id: number) => void
	analyticsModalOpen: boolean
	setAnalyticsModalOpen: (value: boolean) => void
	submitVisitorForm: () => void
	initAnalytics: () => Promise<void>
	formLoading: boolean
}

export interface Profile extends Omit<UserProfile, 'role' | 'region'> {
	user_role: UserRole
	region: Region | null
}

interface Props {
	children: ReactNode
}

export interface BuilderOrder {
	id: number
	name: string
	display_name: string | null
	description: string | null
	component?: () => JSX.Element
	component_id: string
	created_at?: string
	props?: { [key: string]: any }
	draggable: boolean
	active: boolean
	show_in_nav: boolean
	published: boolean
}

export interface BuilderState {
	id: string
	arc_id: string | null
	user_id?: string
	account_manager_id: string
	expiration_date: Date
	created_at?: Date | null
	updated_at?: Date | null
	active: boolean
	status: string
	region: string
	hit_expiration: boolean
	last_login: Date | null
	order: BuilderOrder[]
	username: string
	password: string
	name: string
	template: 'arc-core' | 'intervention'
	language: 'english' | 'spanish'
	standard: 'ccss' | 'teks'
	salesforce_account_id: string | null
	is_test: boolean
	is_demo: boolean
	disable_analytics: boolean
	english_grades: number[]
	spanish_grades: number[]
	english_grades_middle_school: number[]
	english_grades_fsc: number[]
	toolkits: Toolkit[]
	digital_solutions: string[]
	formative_assessment: string[]
	accelerator: string[]
	pl_videos: string[]
	pl_resources: StandardlessResource[]
	additional_resources: Resource[]
}

interface CoreAdoptionResult
	extends Omit<
		CoreAdoption,
		| 'order'
		| 'components'
		| 'status'
		| 'region'
		| 'pl_videos'
		| 'digital_solutions'
		| 'accelerator'
		| 'formative_assessment'
		| 'english_grades'
		| 'spanish_grades'
		| 'english_grades_middle_school'
		| 'english_grades_fsc'
		| 'standard'
		| 'toolkits'
		| 'additional_resources'
		| 'pl_resources'
		| 'language'
		| 'template'
	> {
	order: number[]
	components: number[]
	pl_videos: string[]
	english_grades: number[]
	english_grades_middle_school: number[]
	english_grades_fsc: number[]
	digital_solutions: string[]
	accelerator: string[]
	formative_assessment: string[]
	spanish_grades: number[]
	status: { name: string }
	region: { name: string }
	toolkits: number[]
	additional_resources: number[]
	pl_resources: number[]
	language: 'english' | 'spanish'
	standard: 'ccss' | 'teks'
	template: 'arc-core' | 'intervention'
}

export const builderComponentMatch = [
	{
		id: 1,
		component: AccountManagerBar
	},
	{
		id: 2,
		component: Navigation
	},
	{
		id: 3,
		component: HeroSection
	},
	{
		id: 4,
		component: LanguageToggle
	},
	{
		id: 5,
		component: Videos
	},
	{
		id: 6,
		component: ThemeMap
	},
	{
		id: 7,
		component: ThemeMap
	},
	{
		id: 8,
		component: KnowledgeBuilding
	},
	{
		id: 9,
		component: Phonics
	},
	{
		id: 10,
		component: FormativeAssessment
	},
	{
		id: 11,
		component: EnglishToolkits
	},
	{
		id: 12,
		component: EnglishToolkitsSecondary
	},
	{
		id: 13,
		component: FormativeAssessmentSpanish
	},
	{
		id: 14,
		component: SpanishToolkits
	},
	{
		id: 15,
		component: DayYear
	},
	{
		id: 16,
		component: DigitalSolutionsTabs
	},
	{
		id: 17,
		component: DigitalSolutionsAccess
	},
	{
		id: 18,
		component: ProfessionalLearning
	},
	{
		id: 19,
		component: Reviews
	},
	{
		id: 20,
		component: AdditionalResources
	},
	{
		id: 21,
		component: Accelerator
	},
	{
		id: 22,
		component: HeroSectionIntervention
	},
	{
		id: 23,
		component: VideosIntervention
	},
	{
		id: 24,
		component: OneHundredBookChallengeEnglish
	},
	{
		id: 25,
		component: OneHundredBookChallengeSpanish
	},
	{
		id: 26,
		component: ThemeMapMiddleSchool
	},
	{
		id: 27,
		component: AcceleratorElementary
	},
	{
		id: 28,
		component: AcceleratorSecondary
	},
	{
		id: 29,
		component: FoundationalSkillsToolkits
	},
	{
		id: 30,
		component: ENILToolkits
	},
	{
		id: 31,
		component: IRLAToolkits
	},
	{
		id: 32,
		component: SecondaryToolkits
	},
	{
		id: 33,
		component: IRLAStarterKit
	},
	{
		id: 34,
		component: ENILStarterKit
	},
	{
		id: 35,
		component: Decodables
	},
	{
		id: 36,
		component: ThemeMapFSC
	}
]

async function getBuilderComponents(): Promise<BuilderOrder[]> {
	try {
		console.log('%c Getting Components ', 'background: #2563eb; color: #fff')
		const { data: components, error } = await supabase.from('core_adoption_components').select('*').eq('published', true)
		if (error) throw new Error(error.message)
		const order = components!
			.sort((a, b) => a.id - b.id)
			.map((c) => {
				return {
					...c,
					component: builderComponentMatch.filter((bc) => bc.id === c.id)[0].component,
					component_id: stringToLowerCaseDashString(c.name),
					active: true
				}
			})
		// @ts-ignore
		return order
	} catch (error: any) {
		throw new Error(error.message)
	}
}

async function getToolkits(standard: string) {
	try {
		console.log('%c Getting Toolkits ', 'background: #2563eb; color: #fff')
		const { data: toolkits, error } = await supabase.from('core_adoption_toolkits').select('*').or(`standard.eq.${standard},standard.eq.any`).eq('active', true)
		if (error) throw new Error(error.message)
		return toolkits!
	} catch (error: any) {
		throw new Error(error.message)
	}
}

async function getAdditionalResources(standard: string) {
	try {
		console.log('%c Getting Additional Resources ', 'background: #2563eb; color: #fff')
		const { data: resources, error } = await supabase.from('core_adoption_additional_resources').select('*').eq('active', true).or(`standard.eq.${standard},standard.eq.any`)
		if (error) throw new Error(error.message)
		return resources!
	} catch (error: any) {
		throw new Error(error.message)
	}
}

async function getPlResources() {
	try {
		console.log('%c Getting Resources ', 'background: #2563eb; color: #fff')
		const { data: resources, error } = await supabase.from('core_adoption_pl_resources').select('*').eq('active', true)
		if (error) throw new Error(error.message)
		return resources!
	} catch (error: any) {
		throw new Error(error.message)
	}
}

function mapComponents(account: CoreAdoptionResult, components: BuilderOrder[]) {
	return components
		.sort((a, b) => {
			var indexA = account.order.indexOf(a.id)
			var indexB = account.order.indexOf(b.id)
			return indexA - indexB
		})
		.map((c) => ({
			...c,
			active: !account.components?.includes(c.id)
		}))
}

export const CoreAdoptionContext = createContext<ICoreAdoptionContext>({
	account: null,
	accountManager: null,
	language: 'english',
	setLanguage: () => null,
	standard: 'ccss',
	visitor: { id: null, name: '', email: '', phone: '', title: '' },
	handleSetVisitor: () => null,
	sessionId: null,
	setSessionId: () => null,
	analyticsModalOpen: false,
	setAnalyticsModalOpen: () => null,
	submitVisitorForm: () => null,
	initAnalytics: () => Promise.resolve(),
	formLoading: false
})

const CoreAdoptionProvider = ({ children }: Props) => {
	const user = useUser()
	const [account, setAccount] = useState<BuilderState | null>(null)
	const [accountManager, setAccountManager] = useState<Profile | null>(null)
	const [language, setLanguage] = useState<'english' | 'spanish'>('english')
	const [standard, setStandard] = useState<'ccss' | 'teks'>('ccss')
	const [visitor, setVisitor] = useState<ICoreAdoptionContext['visitor']>({ id: null, name: '', email: '', phone: '', title: '' })
	const [sessionId, setSessionId] = useState<number | null>(null)
	const [analyticsModalOpen, setAnalyticsModalOpen] = useState(false)
	const [formLoading, setFormLoading] = useState(false)

	useEffect(() => {
		if (isBrowser) {
			const localStorageVisitor = localStorage.getItem('coreAdoptionVisitor') || JSON.stringify({ id: null, name: '', email: '', phone: '', title: '' })
			setVisitor(JSON.parse(localStorageVisitor))
		}
	}, [])

	function handleSetVisitor(key: keyof ICoreAdoptionContext['visitor'], value: string) {
		setVisitor((prevState) => ({ ...prevState, [key]: value }))
	}

	// Get the Core Adoption Account
	useEffect(() => {
		async function getAccount(id: string) {
			try {
				console.log('%c Getting Account ', 'background: #2563eb; color: #fff')
				const { data, error } = await supabase
					.from('core_adoptions')
					.select('*, status:core_adoption_statuses (name), region:regions (name)')
					.eq('id', id)
					.returns<CoreAdoptionResult[]>()

				if (error) throw new Error(error.message)
				const account = data[0]

				const components = await getBuilderComponents()

				console.log('%c Getting Account Manager ', 'background: #2563eb; color: #fff')
				const { data: userProfile, error: error2 } = await supabase
					.from('users')
					.select('*, user_role:user_roles(*), region:users_region_fkey(*), secondary_region:users_secondary_region_fkey(*)')
					.eq('id', account.account_manager_id)
					.returns<Profile[]>()

				if (error2) throw new Error(error2.message)
				const accountManager = userProfile[0]
				setAccountManager(accountManager)

				const toolkits = await getToolkits(account.standard)
				const additionalResources = await getAdditionalResources(account.standard)
				const plResources = await getPlResources()

				console.log('%c Mapping Account ', 'background: #2563eb; color: #fff')
				const builderAccount = {
					...account,
					expiration_date: new Date(account.expiration_date),
					created_at: new Date(account.created_at),
					updated_at: account.updated_at ? new Date(account.updated_at) : null,
					last_login: account.last_login ? new Date(account.last_login) : null,
					status: account.status?.name,
					region: account.region?.name,
					order: mapComponents(account, components),
					toolkits: toolkits?.map((t) => ({
						...t,
						active: !account.toolkits?.includes(t.id)
					})),
					additional_resources: additionalResources?.map((r) => ({
						...r,
						active: !account.additional_resources?.includes(r.id)
					})),
					pl_resources: plResources?.map((r) => ({
						...r,
						active: !account.pl_resources?.includes(r.id)
					}))
				}

				setAccount(builderAccount)
				setLanguage(account.language)
				setStandard(account.standard)
			} catch (error) {
				catchError(error)
			}
		}
		if (user) getAccount(user.id)
	}, [user])

	async function submitVisitorForm() {
		try {
			setFormLoading(true)
			let leadId
			let contactId

			// If visitor is not ARC employee
			if (!visitor.email.includes('americanreading.com')) {
				let salesforceAccount
				// Get the SF Account
				if (account?.salesforce_account_id) {
					const { data: sfAccount } = await axios.get(`https://quotes.americanreading.com/api/v1/salesforce/account/${account?.salesforce_account_id}`)
					salesforceAccount = sfAccount
				}

				let accountZipCode = salesforceAccount ? salesforceAccount.address.zipCode : ''
				// Check to see if this user is already a contact
				console.log('%c Checking If Contact ', 'background: #2563eb; color: #fff')
				const { data: sfContactId } = await axios.get(`https://quotes.americanreading.com/api/v1/salesforce/contact/retrieve?email=${visitor.email}`)

				if (!sfContactId) {
					// If they are not a contact, see if they are a lead
					console.log('%c Checking If Lead ', 'background: #2563eb; color: #fff')
					const { data: sfLeadId } = await axios.get(`https://quotes.americanreading.com/api/v1/salesforce/lead/retrieve?email=${visitor.email}`)
					leadId = sfLeadId
					if (!sfLeadId) {
						// If they are not a lead, create a new lead
						console.log('%c Creating Lead ', 'background: #2563eb; color: #fff')
						const { data: newLeadId } = await axios.post('https://quotes.americanreading.com/api/v1/salesforce/lead/create', {
							firstName: visitor.name.split(' ')[0],
							lastName: visitor.name.split(' ').length > 1 ? visitor.name.split(' ')[1] : visitor.name.split(' ')[0],
							email: visitor.email,
							phone: visitor.phone,
							title: visitor.title,
							pageName: 'Core Adoption',
							company: account?.name,
							zipCode: accountZipCode
						})
						leadId = newLeadId
					}
				}
				contactId = sfContactId
			}

			// Create a new user in the database
			console.log('%c Creating Core Adoption User ', 'background: #2563eb; color: #fff')
			const { data: user, error: error1 } = await supabase
				.from('core_adoption_users')
				.insert({
					name: visitor.name,
					email: visitor.email,
					phone: visitor.phone,
					title: visitor.title,
					account_id: visitor.email.includes('americanreading.com') ? 'ac0f67ee-b4e4-4451-9f95-2bb863b634ee' : account?.id,
					salesforce_contact_id: contactId,
					salesforce_lead_id: leadId
				})
				.select()
				.single()
			if (error1) throw new Error(error1.message)
			// Update the session with the new user_id
			console.log('%c Updating Session ', 'background: #2563eb; color: #fff')
			const { error } = await supabase
				.from('core_adoption_sessions')
				.update({
					user_id: user?.id
				})
				.eq('id', sessionId)
			if (error) throw new Error(error.message)
			// Save the user in local storage for next time
			console.log('%c Storing Visitor ', 'background: #2563eb; color: #fff')
			localStorage.setItem('coreAdoptionVisitor', JSON.stringify({ ...visitor, id: user?.id }))
			await wait(2000)
			setAnalyticsModalOpen(false)
			// Update logrocket with the real email and name
			if (account?.id) initLogRocket(account.id, visitor.email, visitor.name)
		} catch (error: any) {
			toast.error(error.message)
		} finally {
			setFormLoading(false)
		}
	}

	async function getLocation() {
		console.log('%c Getting Location ', 'background: #2563eb; color: #fff')
		const response = await fetch('https://ipgeolocation.abstractapi.com/v1/?api_key=8593827fe4bd4a80a98e8d8ca5d25d19', { method: 'GET' })
		const data = await response.json()
		if (response.ok) {
			return data as Json
		} else {
			Sentry.captureException(data, { extra: { function: 'getLocation()' } })
			return null
		}
	}

	async function initAnalytics() {
		try {
			console.log('%c Initializing Analytics ', 'background: #2563eb; color: #fff')
			if (account) {
				const location = await getLocation()
				console.log('%c Creating Session in DB ', 'background: #2563eb; color: #fff')
				const { data, error } = await supabase
					.from('core_adoption_sessions')
					.insert({
						location: location,
						user_id: visitor.id
					})
					.select()
					.single()
				if (error) throw new Error(error.message)
				console.log('%c Setting Session ID ', 'background: #2563eb; color: #fff')
				setSessionId(data.id)
			}
		} catch (error: any) {
			Sentry.captureException(error, { extra: { function: 'initAnalytics()' } })
		}
	}

	return (
		<CoreAdoptionContext.Provider
			value={{
				account,
				accountManager,
				language,
				setLanguage,
				standard,
				visitor,
				handleSetVisitor,
				sessionId,
				setSessionId,
				analyticsModalOpen,
				setAnalyticsModalOpen,
				submitVisitorForm,
				initAnalytics,
				formLoading
			}}
		>
			{children}
		</CoreAdoptionContext.Provider>
	)
}

export default CoreAdoptionProvider
